import React, { useState, useEffect, useRef, useCallback } from 'react';
import { settingsAPI, bankAccountAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import ShareManager from '../components/ShareManager';
import { useTranslation } from 'react-i18next';

const Settings = () => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState({
    companyName: '',
    address: '',
    nip: '',
    regon: '',
    bankAccount: '',
    ksefDemo: true,
    ksefKeyTest: '',
    ksefKey: '',
    email: '',
    phone: '',
    website: '',
    logoUrl: '',
    invoiceLocation: '',
    defaultPaymentDays: 14,
    defaultVatRate: 23,
    incomeTaxRate: 19,
    invoiceFooter: '',
    pdfBackgroundColor: '#FAF9F6',
    pdfLogoWithBorder: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);

  // Bank accounts state
  const [bankAccounts, setBankAccounts] = useState([]);
  const [newAccountName, setNewAccountName] = useState('');
  const [newAccountNumber, setNewAccountNumber] = useState('');
  const [newAccountSwift, setNewAccountSwift] = useState('');
  const [addingAccount, setAddingAccount] = useState(false);

  const { user } = useAuth();
  const canEdit = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'accountant';
  const savedSettingsRef = useRef(null);

  const isDirty = useCallback(() => {
    if (!savedSettingsRef.current) return false;
    return JSON.stringify(settings) !== JSON.stringify(savedSettingsRef.current);
  }, [settings]);

  // Block browser tab close/refresh and in-app navigation when settings are dirty
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty()) {
        e.preventDefault();
      }
    };
    const handleClick = (e) => {
      const link = e.target.closest('a[href]');
      if (link && isDirty()) {
        if (!window.confirm(t('settings.unsavedChanges'))) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClick, true);
    };
  }, [isDirty, t]);

  useEffect(() => {
    fetchSettings();
    fetchBankAccounts();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getCompanySettings();
      // Convert null values to empty strings to avoid React warning
      const settingsData = response.data.settings;
      setSettings({
        companyName: settingsData.companyName || '',
        address: settingsData.address || '',
        nip: settingsData.nip || '',
        regon: settingsData.regon || '',
        bankAccount: settingsData.bankAccount || '',
        ksefDemo: settingsData.ksefDemo !== undefined ? settingsData.ksefDemo : true,
        ksefKeyTest: settingsData.ksefKeyTest || '',
        ksefKey: settingsData.ksefKey || '',
        email: settingsData.email || '',
        phone: settingsData.phone || '',
        website: settingsData.website || '',
        logoUrl: settingsData.logoUrl || '',
        invoiceLocation: settingsData.invoiceLocation || '',
        defaultPaymentDays: settingsData.defaultPaymentDays || 14,
        defaultVatRate: settingsData.defaultVatRate || 23,
        incomeTaxRate: settingsData.incomeTaxRate || 19,
        invoiceFooter: settingsData.invoiceFooter || '',
        pdfBackgroundColor: settingsData.pdfBackgroundColor || '#FAF9F6',
        pdfLogoWithBorder: settingsData.pdfLogoWithBorder !== undefined ? settingsData.pdfLogoWithBorder : true
      });
      savedSettingsRef.current = {
        companyName: settingsData.companyName || '',
        address: settingsData.address || '',
        nip: settingsData.nip || '',
        regon: settingsData.regon || '',
        bankAccount: settingsData.bankAccount || '',
        ksefDemo: settingsData.ksefDemo !== undefined ? settingsData.ksefDemo : true,
        ksefKeyTest: settingsData.ksefKeyTest || '',
        ksefKey: settingsData.ksefKey || '',
        email: settingsData.email || '',
        phone: settingsData.phone || '',
        website: settingsData.website || '',
        logoUrl: settingsData.logoUrl || '',
        invoiceLocation: settingsData.invoiceLocation || '',
        defaultPaymentDays: settingsData.defaultPaymentDays || 14,
        defaultVatRate: settingsData.defaultVatRate || 23,
        incomeTaxRate: settingsData.incomeTaxRate || 19,
        invoiceFooter: settingsData.invoiceFooter || '',
        pdfBackgroundColor: settingsData.pdfBackgroundColor || '#FAF9F6',
        pdfLogoWithBorder: settingsData.pdfLogoWithBorder !== undefined ? settingsData.pdfLogoWithBorder : true
      };
      setError('');
    } catch (err) {
      setError(t('settings.failedToLoad'));
      console.error('Settings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await bankAccountAPI.getAll();
      setBankAccounts(response.data);
    } catch (err) {
      console.error('Failed to fetch bank accounts:', err);
    }
  };

  const handleAddBankAccount = async () => {
    if (!newAccountName.trim() || !newAccountNumber.trim()) return;

    setAddingAccount(true);
    try {
      await bankAccountAPI.create({
        name: newAccountName.trim(),
        accountNumber: newAccountNumber.trim(),
        swift: newAccountSwift.trim() || null
      });
      setNewAccountName('');
      setNewAccountNumber('');
      setNewAccountSwift('');
      await fetchBankAccounts();
      setSuccess(t('settings.bankAccountAdded'));
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToAddBankAccount'));
    } finally {
      setAddingAccount(false);
    }
  };

  const handleDeleteBankAccount = async (id) => {
    if (!window.confirm(t('settings.deleteBankAccountConfirm'))) return;

    try {
      await bankAccountAPI.delete(id);
      await fetchBankAccounts();
      setSuccess(t('settings.bankAccountDeleted'));
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToDeleteBankAccount'));
    }
  };

  const handleSetDefaultBankAccount = async (id) => {
    try {
      await bankAccountAPI.setDefault(id);
      await fetchBankAccounts();
      setSuccess(t('settings.defaultBankAccountUpdated'));
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToSetDefaultBankAccount'));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canEdit) {
      setError(t('settings.noPermission'));
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await settingsAPI.updateCompanySettings(settings);
      savedSettingsRef.current = { ...settings };
      setSuccess(t('settings.settingsUpdated'));
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToUpdate'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError(t('settings.invalidImageType'));
      return;
    }

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      setError(t('settings.logoTooLarge'));
      return;
    }

    setUploadingLogo(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || '/api'}/settings/company/logo`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSettings(prev => ({ ...prev, logoUrl: response.data.logoUrl }));
      setSuccess(t('settings.logoUploaded'));
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToUploadLogo'));
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm(t('settings.deleteLogoConfirm'))) {
      return;
    }

    setUploadingLogo(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${import.meta.env.VITE_API_URL || '/api'}/settings/company/logo`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setSettings(prev => ({ ...prev, logoUrl: '' }));
      setSuccess(t('settings.logoDeleted'));
    } catch (err) {
      setError(err.response?.data?.error || t('settings.failedToDeleteLogo'));
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      setError('');
      setSuccess('');

      const token = localStorage.getItem('token');
      const response = await axios.get(`${import.meta.env.VITE_API_URL || '/api'}/backup/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `firmflow-backup-${Date.now()}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(t('settings.dataExported'));
    } catch (err) {
      setError(t('settings.failedToExport') + ': ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      setError('');
      setSuccess('');

      // Read file
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      // Send to backend
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL || '/api'}/backup/import`,
        {
          data: importData.data,
          options: {
            overwrite: true,
            importSettings: true,
            importInvoices: true,
            importCosts: true,
            importTemplates: true,
            importCounters: true
          }
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Show results
      const results = response.data.results;
      const messages = [];

      if (results.companySettings?.success) messages.push(t('settings.settingsImported'));
      if (results.invoices?.success) messages.push(`${results.invoices.count} ${t('settings.invoicesImported')}`);
      if (results.costs?.success) messages.push(`${results.costs.count} ${t('settings.costsImported')}`);
      if (results.templates?.success) messages.push(`${results.templates.count} ${t('settings.templatesImported')}`);
      if (results.counters?.success) messages.push(`${results.counters.count} ${t('settings.countersImported')}`);

      setSuccess(`${t('settings.importCompleted')}: ${messages.join(', ')}`);

      // Refresh settings
      await fetchSettings();
    } catch (err) {
      setError(t('settings.failedToImport') + ': ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple-500"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300">{t('settings.loadingSettings')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 bg-clip-text text-transparent">
{t('settings.title')}
      </h1>

      <div className="flex justify-between items-start mb-6">
        <p className="text-gray-600 dark:text-gray-400">{t('settings.description')}</p>
        {canEdit && (
          <button
            onClick={handleSubmit}
            className={`font-medium py-2 px-6 rounded-lg shadow-lg transition-all duration-300 flex-shrink-0 ml-4 ${isDirty() ? 'bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white hover:shadow-xl' : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'}`}
            disabled={saving || !isDirty()}
          >
            {saving ? t('common.loading') : t('settings.saveSettings')}
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl mb-6">{error}</div>}
      {success && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200 px-6 py-4 rounded-xl mb-6">{success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">{t('settings.companyInformation')}</div>

          <div className="p-6 space-y-6">
          {/* Company Logo Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.companyLogo')}</label>
            <div className="flex items-start gap-4">
              {/* Logo Preview */}
              <div className="flex-shrink-0">
                {settings.logoUrl ? (
                  <div className="relative group">
                    <img
                      src={`${import.meta.env.VITE_API_URL || '/api'}${settings.logoUrl}`}
                      alt="Company Logo"
                      className="w-32 h-32 object-contain border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 p-2"
                    />
                    {canEdit && (
                      <button
                        type="button"
                        onClick={handleLogoDelete}
                        disabled={uploadingLogo}
                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                        title={t('settings.deleteLogo')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="w-32 h-32 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center bg-gray-50 dark:bg-gray-800">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div className="flex-1">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleLogoUpload}
                  className="hidden"
                  disabled={!canEdit || uploadingLogo}
                />
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={!canEdit || uploadingLogo}
                  className="px-4 py-2 bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white font-medium rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
{uploadingLogo ? t('settings.uploading') : settings.logoUrl ? t('settings.changeLogo') : t('settings.uploadLogo')}
                </button>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {t('settings.logoRecommendation')}<br />
                  {t('settings.logoFormats')}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.companyName')}*</label>
            <input
              type="text"
              name="companyName"
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
              value={settings.companyName}
              onChange={handleChange}
              required
              disabled={!canEdit}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.address')}*</label>
            <textarea
              name="address"
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
              value={settings.address}
              onChange={handleChange}
              rows="2"
              placeholder={t('settings.addressPlaceholder')}
              disabled={!canEdit}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.companyNip')}*</label>
              <input
                type="text"
                name="nip"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.nip}
                onChange={handleChange}
                placeholder="XXX-XXX-XX-XX"
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.companyRegon')}</label>
              <input
                type="text"
                name="regon"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.regon}
                onChange={handleChange}
                placeholder="XXXXXXXXX"
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              className={`space-y-2 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${settings.ksefDemo ? 'border-amber-400 dark:border-amber-500 bg-amber-50/50 dark:bg-amber-900/10' : 'border-gray-200 dark:border-gray-700 opacity-60'}`}
              onClick={() => canEdit && setSettings(prev => ({ ...prev, ksefDemo: true }))}
            >
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                {t('settings.ksefKeyTest')}
                {settings.ksefDemo && <span className="ml-2 text-xs font-semibold text-amber-600 dark:text-amber-400">{t('settings.ksefActive')}</span>}
              </label>
              <input
                type="text"
                name="ksefKeyTest"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 font-mono text-sm"
                value={settings.ksefKeyTest}
                onChange={handleChange}
                onClick={(e) => e.stopPropagation()}
                placeholder={t('settings.ksefKeyPlaceholder')}
                disabled={!canEdit}
              />
              <small className="text-gray-500 dark:text-gray-400">{t('settings.ksefKeyTestHelp')}</small>
            </div>
            <div
              className={`space-y-2 p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${!settings.ksefDemo ? 'border-blue-400 dark:border-blue-500 bg-blue-50/50 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-700 opacity-60'}`}
              onClick={() => canEdit && setSettings(prev => ({ ...prev, ksefDemo: false }))}
            >
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                {t('settings.ksefKey')}
                {!settings.ksefDemo && <span className="ml-2 text-xs font-semibold text-blue-600 dark:text-blue-400">{t('settings.ksefActive')}</span>}
              </label>
              <input
                type="text"
                name="ksefKey"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 font-mono text-sm"
                value={settings.ksefKey}
                onChange={handleChange}
                onClick={(e) => e.stopPropagation()}
                placeholder={t('settings.ksefKeyPlaceholder')}
                disabled={!canEdit}
              />
              <small className="text-gray-500 dark:text-gray-400">{t('settings.ksefKeyHelp')}</small>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.companyBankAccount')}*</label>
            <input
              type="text"
              name="bankAccount"
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
              value={settings.bankAccount}
              onChange={handleChange}
              placeholder="XX XXXX XXXX XXXX XXXX XXXX XXXX"
              disabled={!canEdit}
            />
            <small className="text-gray-500 dark:text-gray-400">{t('settings.defaultBankAccountHelp')}</small>
          </div>

          {/* Bank Accounts List */}
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.additionalBankAccounts')}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">{t('settings.additionalBankAccountsHelp')}</p>

            {/* Add new account */}
            {canEdit && (
              <div className="flex gap-2 flex-wrap">
                <input
                  type="text"
                  value={newAccountName}
                  onChange={(e) => setNewAccountName(e.target.value)}
                  placeholder={t('settings.accountName')}
                  className="flex-1 min-w-[120px] px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                />
                <input
                  type="text"
                  value={newAccountNumber}
                  onChange={(e) => setNewAccountNumber(e.target.value)}
                  placeholder={t('settings.accountNumber')}
                  className="flex-[2] min-w-[180px] px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                />
                <input
                  type="text"
                  value={newAccountSwift}
                  onChange={(e) => setNewAccountSwift(e.target.value.toUpperCase())}
                  placeholder={t('settings.swiftCode')}
                  className="w-[120px] px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 uppercase"
                />
                <button
                  type="button"
                  onClick={handleAddBankAccount}
                  disabled={addingAccount || !newAccountName.trim() || !newAccountNumber.trim()}
                  className="px-4 py-2 bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white font-medium rounded-lg shadow transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingAccount ? t('common.loading') : t('common.add')}
                </button>
              </div>
            )}

            {/* Accounts list */}
            {bankAccounts.length > 0 ? (
              <div className="space-y-2">
                {bankAccounts.map((account) => (
                  <div
                    key={account.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      account.isDefault
                        ? 'bg-pastel-purple-50 dark:bg-pastel-purple-900/20 border-pastel-purple-300 dark:border-pastel-purple-700'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-white">{account.name}</span>
                        {account.isDefault && (
                          <span className="px-2 py-0.5 text-xs bg-pastel-purple-500 text-white rounded-full">
                            {t('common.default')}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-mono">{account.accountNumber}</span>
                        {account.swift && (
                          <span className="text-sm text-gray-500 dark:text-gray-500 font-mono">SWIFT: {account.swift}</span>
                        )}
                      </div>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        {!account.isDefault && (
                          <button
                            type="button"
                            onClick={() => handleSetDefaultBankAccount(account.id)}
                            className="px-3 py-1 text-sm text-pastel-purple-600 hover:text-pastel-purple-700 dark:text-pastel-purple-400 dark:hover:text-pastel-purple-300"
                            title={t('settings.setAsDefault')}
                          >
                            {t('settings.setAsDefault')}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDeleteBankAccount(account.id)}
                          className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          title={t('common.delete')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic">{t('settings.noBankAccounts')}</p>
            )}
          </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">{t('settings.contactInformation')}</div>

          <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.email')}</label>
              <input
                type="email"
                name="email"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.email}
                onChange={handleChange}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('common.phone')}</label>
              <input
                type="tel"
                name="phone"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.phone}
                onChange={handleChange}
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.companyWebsite')}</label>
            <input
              type="url"
              name="website"
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
              value={settings.website}
              onChange={handleChange}
              placeholder="https://your-website.com"
              disabled={!canEdit}
            />
          </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">{t('settings.invoiceSettings')}</div>

          <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.invoiceLocation')}</label>
              <input
                type="text"
                name="invoiceLocation"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.invoiceLocation}
                onChange={handleChange}
                placeholder={t('settings.invoiceLocationPlaceholder')}
                disabled={!canEdit}
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.defaultPaymentDays')}</label>
              <input
                type="number"
                name="defaultPaymentDays"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.defaultPaymentDays}
                onChange={handleChange}
                min="1"
                max="365"
                disabled={!canEdit}
              />
              <small className="text-gray-500 dark:text-gray-400">{t('settings.paymentDaysHelp')}</small>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.defaultVatRate')} (%)</label>
              <input
                type="number"
                step="0.01"
                name="defaultVatRate"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.defaultVatRate}
                onChange={handleChange}
                min="0"
                max="100"
                disabled={!canEdit}
              />
              <small className="text-gray-500 dark:text-gray-400">{t('settings.vatRateHelp')}</small>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.incomeTaxRate')} (%)</label>
              <input
                type="number"
                step="0.01"
                name="incomeTaxRate"
                className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
                value={settings.incomeTaxRate}
                onChange={handleChange}
                min="0"
                max="100"
                disabled={!canEdit}
              />
              <small className="text-gray-500 dark:text-gray-400">{t('settings.incomeTaxRateHelp')}</small>
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.invoiceFooter')}</label>
            <textarea
              name="invoiceFooter"
              className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
              value={settings.invoiceFooter}
              onChange={handleChange}
              rows="3"
              placeholder={t('settings.invoiceFooterPlaceholder')}
              disabled={!canEdit}
            />
          </div>

          {/* PDF Customization */}
          <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('settings.pdfCustomization')}</h3>

            {/* PDF Background Color */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.pdfBackgroundColor')}</label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  name="pdfBackgroundColor"
                  className="h-12 w-24 rounded-lg cursor-pointer border-2 border-gray-300 dark:border-gray-600"
                  value={settings.pdfBackgroundColor}
                  onChange={handleChange}
                  disabled={!canEdit}
                />
                <input
                  type="text"
                  name="pdfBackgroundColor"
                  className="flex-1 px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100 font-mono"
                  value={settings.pdfBackgroundColor}
                  onChange={handleChange}
                  placeholder="#FAF9F6"
                  pattern="^#[0-9A-Fa-f]{6}$"
                  disabled={!canEdit}
                />
              </div>
              <small className="text-gray-500 dark:text-gray-400">{t('settings.pdfBackgroundColorHelp')}</small>
            </div>

            {/* Logo Border Toggle */}
            <div className="space-y-2">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="pdfLogoWithBorder"
                  className="w-5 h-5 text-pastel-purple-500 bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-600 rounded focus:ring-pastel-purple-500 focus:ring-2"
                  checked={settings.pdfLogoWithBorder}
                  onChange={(e) => setSettings(prev => ({ ...prev, pdfLogoWithBorder: e.target.checked }))}
                  disabled={!canEdit}
                />
                <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">{t('settings.pdfLogoWithBorder')}</span>
              </label>
              <small className="text-gray-500 dark:text-gray-400 block ml-8">{t('settings.pdfLogoWithBorderHelp')}</small>
            </div>
          </div>
          </div>
        </div>


        {!canEdit && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 px-6 py-4 rounded-xl mb-6">
{t('settings.needAdminRole')}
          </div>
        )}
      </form>

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">{t('settings.aboutPdfExport')}</div>
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300">{t('settings.pdfExportDescription')}</p>
          <ul className="ml-6 mt-2 text-gray-700 dark:text-gray-300 list-disc space-y-1">
            <li>{t('settings.pdfExportItem1')}</li>
            <li>{t('settings.pdfExportItem2')}</li>
            <li>{t('settings.pdfExportItem3')}</li>
            <li>{t('settings.pdfExportItem4')}</li>
            <li>{t('settings.pdfExportItem5')}</li>
          </ul>
          <p className="mt-4 text-gray-700 dark:text-gray-300">
            {t('settings.pdfExportHowTo')}
          </p>
        </div>
      </div>

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">{t('settings.backupRestore')}</div>
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300">{t('settings.backupRestoreDescription')}</p>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleExport}
              className="bg-pastel-blue-600 hover:bg-pastel-blue-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
              disabled={exporting}
            >
{exporting ? t('settings.exporting') : t('settings.exportData')}
            </button>

            <button
              onClick={handleImportClick}
              className="bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white font-medium py-2 px-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={importing || !canEdit}
            >
{importing ? t('settings.importing') : t('settings.importData')}
            </button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".json"
              style={{ display: 'none' }}
            />
          </div>

          {!canEdit && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200 px-4 py-3 rounded-lg mt-4">
{t('settings.needAdminRoleImport')}
            </div>
          )}

          <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
            <strong>{t('settings.note')}:</strong> {t('settings.backupNote')}
          </div>
        </div>
      </div>

      <PaletteSelector />

      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
        <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">{t('settings.resourceSharing')}</div>
        <div className="p-6">
          <p className="text-gray-700 dark:text-gray-300">{t('settings.resourceSharingDescription')}</p>
          <div className="mt-6">
            <ShareManager />
          </div>
        </div>
      </div>
    </div>
  );
};

// Palette Selector Component
const PaletteSelector = () => {
  const { t } = useTranslation();
  const { currentPalette, setPalette, availablePalettes } = useTheme();

  return (
    <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border border-pastel-purple-200 dark:border-gray-700 overflow-hidden mb-6">
      <div className="bg-gradient-to-r from-pastel-purple-500 to-pastel-blue-500 px-6 py-4 text-white font-bold text-xl">
{t('settings.colorPalette')}
      </div>
      <div className="p-6">
        <p className="text-gray-700 dark:text-gray-300 mb-6">
          {t('settings.colorPaletteDescription')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(availablePalettes).map(([key, palette]) => (
            <button
              key={key}
              onClick={() => setPalette(key)}
              className={`relative p-4 rounded-xl border-2 transition-all ${
                currentPalette === key
                  ? 'border-pastel-purple-500 dark:border-pastel-purple-400 shadow-lg scale-105'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {/* Palette name and description */}
              <div className="mb-3">
                <h3 className="font-semibold text-gray-900 dark:text-white">{palette.name}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{palette.description}</p>
              </div>

              {/* Color preview */}
              <div className="flex gap-1 mb-2">
                {Object.values(palette.primary).slice(3, 8).map((color, idx) => (
                  <div
                    key={idx}
                    className="flex-1 h-8 rounded"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Accent color preview */}
              <div className="flex gap-1">
                {Object.values(palette.accent).slice(3, 8).map((color, idx) => (
                  <div
                    key={idx}
                    className="flex-1 h-4 rounded"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Selected indicator */}
              {currentPalette === key && (
                <div className="absolute top-2 right-2 w-6 h-6 bg-pastel-purple-500 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          <strong>{t('settings.tip')}:</strong> {t('settings.colorPaletteTip')}
        </div>
      </div>
    </div>
  );
};

export default Settings;
